import {
  EnvironmentProviders,
  inject,
  InjectionToken,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { FirebaseApp, FirebaseOptions, getApp, getApps, initializeApp } from 'firebase/app';
import { Auth, connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, Firestore, getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, Functions, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, FirebaseStorage, getStorage } from 'firebase/storage';
import { connectDatabaseEmulator, Database, getDatabase } from 'firebase/database';

import { environment } from '../../environments/environment';

// Default region for Cloud Functions calls.
// (Functions can be deployed to other regions and accessed via getFunctions(app, region).)
const FUNCTIONS_REGION = 'europe-west1';

// Firestore database ID. We use a named database called `ghosts` (not the
// reserved `(default)` database) because Firestore Enterprise edition cannot
// provision the `(default)` database. This must match `firebase.json`'s
// `firestore.database` field. Keep in sync if it ever changes.
const FIRESTORE_DATABASE_ID = 'ghosts';

// Emulator endpoints — kept aligned with firebase.json's `emulators` block.
const EMULATOR_HOST = 'localhost';
const EMULATOR_PORTS = {
  auth: 9099,
  firestore: 8080,
  functions: 5001,
  storage: 9199,
  database: 9000,
} as const;

/**
 * Where Storage serves `?alt=media&token=` downloads — the same origin
 * `getDownloadURL` puts in the URLs it returns, so a URL built from a
 * token (`StorageService.tokenUrl`) is byte-identical to one fetched, and
 * shares its browser-cache entry.
 */
export const STORAGE_DOWNLOAD_ORIGIN = environment.useEmulators
  ? `http://${EMULATOR_HOST}:${EMULATOR_PORTS.storage}`
  : 'https://firebasestorage.googleapis.com';

export interface FirebaseHandles {
  readonly app: FirebaseApp;
  readonly auth: Auth;
  readonly firestore: Firestore;
  readonly functions: Functions;
  readonly storage: FirebaseStorage;
  readonly database: Database;
}

export const FIREBASE_HANDLES = new InjectionToken<FirebaseHandles>('FIREBASE_HANDLES');

/**
 * Initializes Firebase once per app and registers all SDK handles in DI.
 *
 * - Reads `environment.firebase` for project config.
 * - When `environment.useEmulators` is true, connects every SDK to the local
 *   Emulator Suite (see firebase.json for ports).
 *
 * Usage:
 * ```ts
 * // app.config.ts
 * providers: [provideFirebase()]
 * ```
 */
export function provideFirebase(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: FIREBASE_HANDLES,
      useFactory: createFirebaseHandles,
    },
    // Force initialization at bootstrap so emulator connections are wired
    // before any component or route guard runs.
    provideAppInitializer(() => {
      inject(FIREBASE_HANDLES);
    }),
  ]);
}

function createFirebaseHandles(): FirebaseHandles {
  const options: FirebaseOptions = { ...environment.firebase };
  // Reuse the default app if it already exists. Re-initializing throws and
  // breaks unit tests that spin up multiple Angular root injectors, HMR, and
  // any scenario that re-bootstraps the app within the same JS context.
  const isFirstInit = getApps().length === 0;
  const app = isFirstInit ? initializeApp(options) : getApp();

  const auth = getAuth(app);
  const firestore = getFirestore(app, FIRESTORE_DATABASE_ID);
  const functions = getFunctions(app, FUNCTIONS_REGION);
  const storage = getStorage(app);
  const database = getDatabase(app);

  // Emulator connectors must only run once per SDK singleton — calling them
  // twice throws ("instance has already been used"). Since getAuth/getFirestore
  // return cached singletons keyed by app, gating on `isFirstInit` is enough.
  if (isFirstInit && environment.useEmulators) {
    connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, {
      disableWarnings: true,
    });
    connectFirestoreEmulator(firestore, EMULATOR_HOST, EMULATOR_PORTS.firestore);
    connectFunctionsEmulator(functions, EMULATOR_HOST, EMULATOR_PORTS.functions);
    connectStorageEmulator(storage, EMULATOR_HOST, EMULATOR_PORTS.storage);
    connectDatabaseEmulator(database, EMULATOR_HOST, EMULATOR_PORTS.database);
  }

  return { app, auth, firestore, functions, storage, database };
}
